import { fetchServer } from "@/features/utils/fetch";
import { utils } from "@passwordless-id/webauthn";

const webauthn_uri = {
  urlAuthenticationOptions: "/webauthn/login/options",
  urlVerifyUserAuthentication: "/webauthn/login/verify"
};
type clientAssertionResponse = {
  authenticatorAttachment: string;
  id: string;
  rawId: string;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle: string;
  };
};
type AuthenticatorAssertionResponse = {
  authenticatorData: ArrayBuffer;
  clientDataJSON: ArrayBuffer;
  signature: ArrayBuffer;
  userHandle: ArrayBuffer;
};
export class WebAuthnApi {
  /**
   *
   */
  static RECAPTCHA_TOKEN_AUTHENTICATION_OPTIONS: string | null = null;
  static async getAuthenticationOptions(email: string, purpose: string) {
    const response = await fetchServer(webauthn_uri.urlAuthenticationOptions, {
      method: "POST",
      mainRoot: true,
      body: {
        username: email,
        purpose,
        captcha_token: WebAuthnApi.RECAPTCHA_TOKEN_AUTHENTICATION_OPTIONS
      }
    });
    WebAuthnApi.setCaptchaForAuthenticationOptions(null);
    if (response.status === 429) {
      throw new Error("Rate limit hit", { cause: { status: response.status } });
    }
    if (response.status === 200) {
      const res = await response.json();
      return res.data as {
        twoFa: boolean;
        sms: boolean;
        webauthn: boolean;
        options: {
          uuid: string;
          challenge: string;
          timeout: number;
          rpId: string;
          userVerification: UserVerificationRequirement;
          allowCredentials: [
            {
              credential_id: string;
            }
          ];
        };
      };
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid getAuthenticationOptions");
    }
  }
  /**
   *
   * @param email
   * @param uuid
   * @param clientAssertionResponse
   * @param isSolidAuth
   */
  static async verifyUserAuthentication(
    email: string,
    uuid: string,
    clientAssertionResponse: clientAssertionResponse,
    isSolidAuth?: boolean
  ) {
    const response = await fetchServer(webauthn_uri.urlVerifyUserAuthentication, {
      method: "POST",
      mainRoot: true,
      body: {
        username: email,
        uuid: uuid,
        clientAssertionResponse: clientAssertionResponse,
        isSolidAuth: isSolidAuth
      }
    });
    if (response.status === 200) {
      const res = await response.json();
      return res.data as {
        login: boolean;
        token: string;
      }; // Return valid data
    } else {
      // Handle cases where data is empty or not valid
      throw new Error("Invalid verifyUserAuthentication");
    }
  }

  /**
   *
   * @param inputBase64
   */
  static decodeBase64(inputBase64: string) {
    // Replace URL-safe characters
    let base64 = inputBase64.replace(/-/g, "+").replace(/_/g, "/");
    // Add padding if needed
    while (base64.length % 4 !== 0) {
      base64 += "=";
    }
    return base64;
  }

  /**
   *
   */
  static async authenticateWebAuthnUser(
    email: string,
    purpose?: string,
    isSolidAuth?: boolean
  ): Promise<string> {
    try {
      const response = await WebAuthnApi.getAuthenticationOptions(email, purpose ?? "login");
      if (!response.webauthn) {
        throw new Error("No active webAuthn");
      }
      const authnOptions = response.options;
      const challenge = utils.toBuffer(authnOptions.challenge);

      const allowCredentials: PublicKeyCredentialDescriptor[] = authnOptions.allowCredentials.map(
        (credential) => ({
          type: "public-key",
          id: new Uint8Array(
            atob(WebAuthnApi.decodeBase64(credential.credential_id))
              .split("")
              .map((char) => char.charCodeAt(0))
          )
        })
      );

      const options: PublicKeyCredentialRequestOptions = {
        ...authnOptions,
        userVerification: authnOptions.userVerification,
        challenge: challenge,
        allowCredentials
      };

      const rawClientAssertionResponse = (await navigator.credentials.get({
        publicKey: options,
        mediation: "required"
      })) as unknown as {
        id: string;
        rawId: ArrayBuffer;
        response: AuthenticatorAssertionResponse;
        type: string;
        authenticatorAttachment: string;
      };
      /**
       * i did ... but won't work
       * ...rawClientAssertionResponse,
       */
      const clientAssertionResponse = {
        authenticatorAttachment: rawClientAssertionResponse?.authenticatorAttachment,
        id: rawClientAssertionResponse?.id,
        rawId: utils.toBase64url(rawClientAssertionResponse?.rawId),
        response: {
          authenticatorData: utils.toBase64url(
            rawClientAssertionResponse?.response?.authenticatorData
          ),
          clientDataJSON: utils.toBase64url(rawClientAssertionResponse?.response?.clientDataJSON),
          signature: utils.toBase64url(rawClientAssertionResponse?.response?.signature),
          userHandle: utils.toBase64url(rawClientAssertionResponse?.response?.userHandle)
        }
      };

      const authnResult = await WebAuthnApi.verifyUserAuthentication(
        email,
        authnOptions.uuid,
        clientAssertionResponse,
        isSolidAuth
      );
      if (isSolidAuth) return authnResult.token;

      return authnResult?.token;
    } catch (_) {
      throw new Error("Access key authentication failed!");
    }
  }

  static setCaptchaForAuthenticationOptions(token: string | null) {
    WebAuthnApi.RECAPTCHA_TOKEN_AUTHENTICATION_OPTIONS = token; // Method to set the token
  }
}
